/**
 * Servicio para manejar la funcionalidad de Networking
 */

import { firebase } from '../config/firebase.config.js';
import { Storage } from '@capacitor/storage';
import { NetworkingProfile, NetworkingScan, QRCodeData, NetworkingStats } from '../models/Networking';
import { getAppConfig } from '../config/app.config';
import NetworkingApiService from './NetworkingApiService';
import { configService } from './ConfigService';

// Tipos de errores específicos para networking
export class ExistingConnectionError extends Error {
  constructor(message: string, public existingProfile: NetworkingProfile) {
    super(message);
    this.name = 'ExistingConnectionError';
  }
}

export class UserNotFoundError extends Error {
  constructor(message: string, public cedula: string) {
    super(message);
    this.name = 'UserNotFoundError';
  }
}

export class SelfScanError extends Error {
  constructor(message: string, public userProfile: NetworkingProfile) {
    super(message);
    this.name = 'SelfScanError';
  }
}

export class NetworkingService {
  private static instance: NetworkingService;
  private currentProfile: NetworkingProfile | null = null;

  private constructor() {}

  static getInstance(): NetworkingService {
    if (!NetworkingService.instance) {
      NetworkingService.instance = new NetworkingService();
    }
    return NetworkingService.instance;
  }

  /** UID verificable que las reglas de Firestore usan como propietario. */
  private getAuthenticatedFirebaseUid(): string {
    const currentUser = firebase.auth().currentUser;
    if (!currentUser?.uid) {
      throw new Error('La sesión de Firebase no está disponible para networking.');
    }
    return currentUser.uid;
  }

  /**
   * Inicializar el perfil de networking del usuario
   */
  async initializeProfile(userData: {
    id: string;
    name: string;
    email: string;
    phone?: string;
    company?: string;
    position?: string;
    profilePhotoUrl?: string;
    eventCode: string;
  }): Promise<void> {
    try {
      console.log('NetworkingService: Inicializando perfil para usuario:', userData.id);

      // Debugging completo del estado de autenticación
      await this.debugAuthenticationState();

      // Verificar autenticación de Firebase en detalle
      const currentUser = firebase.auth().currentUser;
      console.log('NetworkingService: Usuario Firebase actual:', currentUser ? 'Autenticado' : 'No autenticado');

      if (currentUser) {
        console.log('NetworkingService: UID de Firebase:', currentUser.uid);
        console.log('NetworkingService: Email de Firebase:', currentUser.email);
        console.log('NetworkingService: Es anónimo:', currentUser.isAnonymous);

      } else {
        console.log('NetworkingService: No hay usuario autenticado en Firebase');

        // Verificar si hay token en Storage
        const { Storage } = await import('@capacitor/storage');
        const tokenResult = await Storage.get({ key: 'firebase_token' });
        console.log('NetworkingService: Token en Storage:', tokenResult.value ? 'Existe' : 'No existe');

      }

      const profile: NetworkingProfile = {
        id: userData.id,
        name: userData.name,
        email: userData.email,
        phone: userData.phone || '',
        company: userData.company || '',
        position: userData.position || '',
        linkedin: '',
        profilePhotoUrl: userData.profilePhotoUrl || '',
        eventCode: userData.eventCode,
        createdAt: new Date(),
        updatedAt: new Date()
      };

      console.log('NetworkingService: Intentando guardar en ruta:', `networking_profiles/${userData.eventCode}/${userData.id}`);
      console.log('NetworkingService: Datos del perfil a guardar:', profile);

      // Verificar reglas de Firebase antes de guardar
      const dbRef = firebase.database().ref(`networking_profiles/${userData.eventCode}/${userData.id}`);
      console.log('NetworkingService: Referencia de base de datos creada');

      // Intentar una operación de lectura primero para verificar permisos
      try {
        console.log('NetworkingService: Verificando permisos de lectura...');
        const readTest = await dbRef.once('value');
        console.log('NetworkingService: Lectura exitosa, datos existentes:', readTest.exists());
      } catch (readError) {
        console.error('NetworkingService: Error en lectura (puede ser normal si no existe):', readError);
      }

      // Guardar en Firebase Realtime Database
      console.log('NetworkingService: Iniciando escritura en Firebase...');
      await dbRef.set({
        ...profile,
        createdAt: firebase.database.ServerValue.TIMESTAMP,
        updatedAt: firebase.database.ServerValue.TIMESTAMP
      });
      console.log('NetworkingService: Escritura en Firebase exitosa');

      // Guardar localmente
      await Storage.set({
        key: 'networking_profile',
        value: JSON.stringify(profile)
      });

      this.currentProfile = profile;
      console.log('NetworkingService: Perfil inicializado exitosamente');
    } catch (error) {
      console.error('NetworkingService: Error al inicializar perfil:', error);
      throw error;
    }
  }

  /**
   * Verificar estado completo de autenticación para debugging
   */
  private async debugAuthenticationState(): Promise<void> {
    console.log('=== DEBUGGING AUTHENTICATION STATE ===');

    // 1. Estado de Firebase Auth
    const currentUser = firebase.auth().currentUser;
    console.log('1. Firebase Auth User:', currentUser ? {
      uid: currentUser.uid,
      email: currentUser.email,
      isAnonymous: currentUser.isAnonymous,
      providerId: currentUser.providerId
    } : 'No autenticado');

    // 2. Estado de Storage
    const { Storage } = await import('@capacitor/storage');
    const [tokenResult, payloadResult, expiryResult] = await Promise.all([
      Storage.get({ key: 'firebase_token' }),
      Storage.get({ key: 'firebase_token_payload' }),
      Storage.get({ key: 'firebase_token_expiry' })
    ]);

    console.log('2. Storage State:', {
      hasToken: !!tokenResult.value,
      hasPayload: !!payloadResult.value,
      hasExpiry: !!expiryResult.value
    });

    // 3. Estado de FirebaseTokenService
    try {
      const FirebaseTokenService = (await import('./FirebaseTokenService')).default;
      const tokenService = FirebaseTokenService.getInstance();
      console.log('4. TokenService authenticated:', tokenService.isAuthenticated());
    } catch (e) {
      console.error('Error checking TokenService:', e);
    }

    console.log('=== END DEBUGGING ===');
  }

  /**
   * Obtener el perfil actual del usuario (con inicialización automática si no existe)
   */
  async getCurrentProfile(): Promise<NetworkingProfile | null> {
    if (this.currentProfile) {
      return this.currentProfile;
    }

    try {
      const stored = await Storage.get({ key: 'networking_profile' });
      if (stored.value) {
        this.currentProfile = JSON.parse(stored.value);
        return this.currentProfile;
      }
    } catch (error) {
      console.error('NetworkingService: Error al obtener perfil local:', error);
    }

    // Si no hay perfil local, intentar inicializar automáticamente
    console.log('NetworkingService: No hay perfil local, intentando inicializar automáticamente...');
    try {
      await this.initializeCurrentUserProfile();
      return this.currentProfile;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      console.warn('NetworkingService: No se pudo inicializar perfil automáticamente:', errorMessage);
      console.log('NetworkingService: El usuario debe estar logueado para usar networking');
      return null;
    }
  }

  /**
   * Inicializar perfil del usuario actual desde datos de sesión
   */
  private async initializeCurrentUserProfile(): Promise<void> {
    try {
      // Obtener datos del usuario actual desde Storage
      const userData = await this.getCurrentUserData();
      if (!userData) {
        throw new Error('No hay datos de usuario en sesión');
      }

      console.log('NetworkingService: Inicializando perfil para usuario:', userData);

      try {
        // Buscar o crear usuario en Firebase Realtime Database
        console.log('NetworkingService: Buscando usuario en Firebase Realtime Database');

        const profileFromFirebase = await this.findOrCreateUserProfileByCedula(userData.ced, userData.eventcode);
        console.log('NetworkingService: Perfil obtenido desde Firebase:', profileFromFirebase);

        // Guardar perfil en Storage local
        await Storage.set({
          key: 'networking_profile',
          value: JSON.stringify(profileFromFirebase)
        });

        this.currentProfile = profileFromFirebase;
        console.log('NetworkingService: Perfil inicializado exitosamente desde Firebase');
      } catch (error) {
        console.warn('NetworkingService: Usuario no encontrado en BD esimposio, creando perfil básico:', error);

        // Crear perfil básico con datos disponibles del usuario logueado
        const basicProfile: NetworkingProfile = {
          id: userData.ced,
          name: userData.username || `Usuario ${userData.ced}`,
          email: '',
          phone: '',
          company: '',
          position: '',
          linkedin: '',
          profilePhotoUrl: '',
          eventCode: userData.eventcode,
          createdAt: new Date(),
          updatedAt: new Date()
        };

        // Guardar perfil básico en Storage local
        await Storage.set({
          key: 'networking_profile',
          value: JSON.stringify(basicProfile)
        });

        this.currentProfile = basicProfile;
        console.log('NetworkingService: Perfil básico creado exitosamente:', basicProfile);
      }
    } catch (error) {
      console.error('NetworkingService: Error al inicializar perfil del usuario actual:', error);
      throw error;
    }
  }

  /**
   * Obtener datos del usuario actual desde Storage (usando las claves individuales)
   */
  private async getCurrentUserData(): Promise<any> {
    try {
      console.log('NetworkingService: Obteniendo datos de usuario desde Storage...');

      // Obtener datos usando las claves correctas de Capacitor Storage
      const [
        usernameResult,
        usercedResult,
        useridResult,
        eventcodeResult
      ] = await Promise.all([
        Storage.get({ key: 'username' }),
        Storage.get({ key: 'userced' }),
        Storage.get({ key: 'userid' }),
        Storage.get({ key: 'eventcode' })
      ]);

      console.log('NetworkingService: Resultados de Storage:', {
        username: usernameResult.value,
        userced: usercedResult.value,
        userid: useridResult.value,
        eventcode: eventcodeResult.value
      });

      const userData = {
        username: usernameResult.value,
        ced: usercedResult.value,
        userid: useridResult.value,
        eventcode: eventcodeResult.value
      };

      console.log('NetworkingService: Datos de usuario procesados:', userData);

      // Verificar que tenemos los datos mínimos necesarios
      if (!userData.ced || !userData.eventcode) {
        console.warn('NetworkingService: Faltan datos críticos del usuario. Cédula:', userData.ced, 'EventCode:', userData.eventcode);
        return null;
      }

      return userData;
    } catch (error) {
      console.error('NetworkingService: Error al obtener datos de usuario:', error);
      return null;
    }
  }

  /**
   * Buscar usuario en Realtime Database
   */
  private async findUserInRealtimeDatabase(cedula: string, eventCode: string): Promise<any> {
    try {
      const usersSnapshot = await firebase.database().ref('/user').once('value');

      if (usersSnapshot.exists()) {
        const allUsers = usersSnapshot.val();

        for (const userId in allUsers) {
          const userData = allUsers[userId];
          if (userData.ced === cedula && userData.eventcode === eventCode) {
            return {
              id: userData.ced,
              name: userData.username || '',
              email: userData.email || '',
              phone: userData.phone || '',
              company: userData.company || '',
              position: userData.position || userData.role || '',
              eventCode: eventCode
            };
          }
        }
      }

      return null;
    } catch (error) {
      console.error('NetworkingService: Error al buscar en Realtime Database:', error);
      return null;
    }
  }

  /**
   * Actualizar el perfil de networking
   */
  async updateProfile(updates: Partial<NetworkingProfile>): Promise<void> {
    try {
      const currentProfile = await this.getCurrentProfile();
      if (!currentProfile) {
        throw new Error('No hay perfil actual para actualizar');
      }

      const updatedProfile = {
        ...currentProfile,
        ...updates,
        updatedAt: new Date()
      };

      // Actualizar en Firebase
      await firebase.database().ref(`networking_profiles/${currentProfile.eventCode}/${currentProfile.id}`).update({
        ...updates,
        updatedAt: firebase.database.ServerValue.TIMESTAMP
      });

      // Actualizar localmente
      await Storage.set({
        key: 'networking_profile',
        value: JSON.stringify(updatedProfile)
      });

      this.currentProfile = updatedProfile;
      console.log('NetworkingService: Perfil actualizado exitosamente');
    } catch (error) {
      console.error('NetworkingService: Error al actualizar perfil:', error);
      throw error;
    }
  }

  /**
   * Generar QR code data para el usuario actual (solo cédula, igual que en gafete)
   */
  async generateQRCodeData(): Promise<string> {
    try {
      const profile = await this.getCurrentProfile();
      if (!profile) {
        throw new Error('No hay perfil de networking configurado');
      }

      // Retornar solo la cédula (igual que en gafete)
      return profile.id;
    } catch (error) {
      console.error('NetworkingService: Error al generar QR code:', error);
      throw error;
    }
  }

  /**
   * Procesar un QR code escaneado
   */
  async processScannedQR(qrContent: string): Promise<NetworkingProfile> {
    try {
      console.log('NetworkingService: Procesando QR escaneado:', qrContent);

      // El QR contiene directamente la cédula (igual que en gafete)
      const scannedCedula = qrContent.trim();

      if (!scannedCedula) {
        throw new Error('QR vacío o inválido');
      }

      const currentProfile = await this.getCurrentProfile();
      if (!currentProfile) {
        throw new Error('No hay perfil de networking configurado');
      }

      console.log('NetworkingService: Perfil actual:', {
        id: currentProfile.id,
        name: currentProfile.name,
        eventCode: currentProfile.eventCode
      });

      if (scannedCedula === currentProfile.id) {
        throw new SelfScanError('No puedes escanearte a ti mismo', currentProfile);
      }

      console.log('NetworkingService: Buscando usuario con cédula:', scannedCedula, 'en evento:', currentProfile.eventCode);

      // Buscar el usuario escaneado en Firebase (o crearlo si no existe)
      console.log('NetworkingService: Buscando usuario escaneado en Firebase:', scannedCedula);

      try {
        // Buscar o crear usuario en Firebase Realtime Database
        const scannedProfile = await this.findOrCreateUserProfileByCedula(scannedCedula, currentProfile.eventCode);

        // Verificar si ya existe una conexión en Firestore
        const existingProfile = await this.checkExistingConnection(
          this.getAuthenticatedFirebaseUid(),
          scannedProfile.id,
          currentProfile.eventCode
        );

        if (existingProfile) {
          throw new ExistingConnectionError('Ya tienes una conexión con este usuario', existingProfile);
        }

        // Registrar la conexión en Firestore (NO en MySQL)
        await this.recordConnection(currentProfile, scannedProfile);
        console.log('NetworkingService: Conexión guardada exitosamente en Firestore');

        return scannedProfile;
      } catch (apiError) {
        console.error('NetworkingService: Error al buscar usuario:', apiError);

        // Conservar los errores reales. En particular, un fallo de sesión o del
        // servidor no debe presentarse como si el participante no existiera.
        if (apiError instanceof Error) {
          throw apiError;
        }

        throw new Error('No se pudo procesar la consulta de networking');
      }
    } catch (error) {
      console.error('NetworkingService: Error al procesar QR:', error);
      throw error;
    }
  }

  /**
   * Buscar usuario por cédula en Realtime Database, si no existe, consultarlo en login.php y crearlo
   */
  private async findOrCreateUserProfileByCedula(cedula: string, eventCode: string): Promise<NetworkingProfile> {
    try {
      console.log('NetworkingService: Buscando usuario por cédula en Realtime Database:', cedula);

      // Buscar en la estructura de usuarios de Firebase (igual que en login)
      console.log('NetworkingService: Consultando /user en Firebase...');
      const usersSnapshot = await firebase.database().ref('/user').once('value');

      if (usersSnapshot.exists()) {
        const allUsers = usersSnapshot.val();
        console.log('NetworkingService: Usuarios encontrados en Firebase:', Object.keys(allUsers).length);

        // Buscar usuario por cédula
        let foundUser = false;
        for (const userId in allUsers) {
          const userData = allUsers[userId];
          console.log(`NetworkingService: Verificando usuario ${userId}:`, {
            ced: userData.ced,
            eventcode: userData.eventcode,
            username: userData.username
          });

          if (userData.ced === cedula && userData.eventcode === eventCode) {
            console.log('NetworkingService: ¡Usuario encontrado en Realtime Database!');
            console.log('NetworkingService: ID del nodo:', userId);
            console.log('NetworkingService: Datos completos del usuario:', userData);
            console.log('NetworkingService: Campos específicos:');
            console.log('  - ced:', userData.ced);
            console.log('  - username:', userData.username);
            console.log('  - email:', userData.email);
            console.log('  - phone:', userData.phone);
            console.log('  - company:', userData.company);
            console.log('  - role:', userData.role);
            console.log('  - eventcode:', userData.eventcode);
            foundUser = true;

            // Convertir datos de usuario a perfil de networking
            const profile: NetworkingProfile = {
              id: userData.ced,
              name: userData.username || '',
              email: userData.email || '',
              phone: userData.phone || '',
              company: userData.company || '',
              position: userData.role || '',
              linkedin: '',
              profilePhotoUrl: userData.profilePhotoUrl || '',
              eventCode: eventCode,
              createdAt: new Date(),
              updatedAt: new Date()
            };

            console.log('NetworkingService: Perfil creado desde Firebase:', {
              id: profile.id,
              name: profile.name,
              email: profile.email,
              phone: profile.phone,
              company: profile.company,
              position: profile.position
            });

            return profile;
          }
        }

        if (!foundUser) {
          console.log(`NetworkingService: Usuario con cédula ${cedula} y evento ${eventCode} NO encontrado en Firebase`);
        }
      } else {
        console.log('NetworkingService: No hay usuarios en /user de Firebase');
      }

      console.log('NetworkingService: Usuario no encontrado en Realtime Database, consultando login.php');

      // Si no existe, consultar login.php y crear en Realtime Database (igual que en login)
      const userDataFromAPI = await this.fetchUserFromAPI(cedula, eventCode);

      // Crear usuario en Realtime Database (igual que en login)
      const newProfile = await this.createUserInRealtimeDatabase(userDataFromAPI, eventCode);

      console.log('NetworkingService: Usuario creado en Realtime Database desde API:', newProfile);
      return newProfile;

    } catch (error) {
      console.error('NetworkingService: Error al buscar/crear usuario por cédula:', error);
      throw error;
    }
  }

  /**
   * Consultar datos del usuario en login.php
   */
  private async fetchUserFromAPI(userId: string, eventCode: string): Promise<any> {
    try {
      console.log('NetworkingService: Consultando API para usuario:', userId);

      // Verificar conexión a internet
      if (!navigator.onLine) {
        throw new Error('No hay conexión a internet para consultar el usuario');
      }

      // En modo correo el token identifica al usuario que consulta y lookupCed
      // conserva la cédula del QR. El modo cédula mantiene su contrato legacy.
      let idToken: string | undefined;
      if (configService.usesEmailPasswordAuthentication()) {
        const currentUser = firebase.auth().currentUser;
        if (!currentUser) {
          throw new Error('La sesión de acceso expiró. Inicie sesión nuevamente.');
        }
        idToken = await currentUser.getIdToken();
      }

      const requestData = configService.buildNetworkingLookupRequest(userId, eventCode, idToken);
      const requestOptions = configService.getLoginRequestOptions(requestData);

      const response = await fetch(configService.getLoginEndpoint(), requestOptions);
      let data: any;
      try {
        data = await response.json();
      } catch (_) {
        throw new Error('El servidor devolvió una respuesta inválida');
      }

      if (!response.ok || data.error) {
        const errorCode = typeof data.code === 'string' ? data.code : '';
        const serverMessage = typeof data.msg === 'string'
          ? data.msg
          : typeof data.message === 'string'
            ? data.message
            : typeof data.error === 'string'
              ? data.error
              : '';

        if (response.status === 404 || errorCode === 'PARTICIPANT_NOT_FOUND' ||
            /not found|no encontrado/i.test(serverMessage)) {
          throw new UserNotFoundError(
            `No se pudo encontrar el usuario con cédula ${userId}`,
            userId
          );
        } else if (response.status === 401 || response.status === 403 ||
                   errorCode === 'FIREBASE_AUTH_REQUIRED' ||
                   errorCode === 'INVALID_FIREBASE_TOKEN') {
          throw new Error('No se pudo validar la sesión para consultar el participante.');
        } else if (response.status >= 500) {
          throw new Error('Error del servidor. Inténtalo de nuevo más tarde');
        } else {
          throw new Error(serverMessage || errorCode || `Error de conexión: ${response.status}`);
        }
      }

      // Validar que tenemos datos mínimos necesarios
      if (!data.id && !data.ced) {
        throw new Error('Datos de usuario incompletos en el servidor');
      }

      console.log('NetworkingService: ===== DATOS COMPLETOS DE LA API =====');
      console.log('NetworkingService: Respuesta completa:', data);
      console.log('NetworkingService: Campos específicos:');
      console.log('  - id:', data.id);
      console.log('  - ced:', data.ced);
      console.log('  - username:', data.username);
      console.log('  - texto1 (Nombre):', data.texto1);
      console.log('  - texto2 (Apellido):', data.texto2);
      console.log('  - texto3 (Email):', data.texto3, '| Tipo:', typeof data.texto3, '| Vacío:', !data.texto3);
      console.log('  - texto4:', data.texto4);
      console.log('  - texto5 (Cédula):', data.texto5);
      console.log('  - texto6:', data.texto6);
      console.log('  - texto7:', data.texto7);
      console.log('  - texto8 (Empresa):', data.texto8);
      console.log('  - texto9 (Teléfono):', data.texto9, '| Tipo:', typeof data.texto9, '| Vacío:', !data.texto9);
      console.log('  - company:', data.company);
      console.log('  - email:', data.email);
      console.log('  - phone:', data.phone);
      console.log('  - eventcode:', data.eventcode);
      console.log('  - tipo_participacion:', data.tipo_participacion);
      console.log('NetworkingService: ===== FIN DATOS API =====');

      return data;

    } catch (error) {
      console.error('NetworkingService: Error al consultar API:', error);

      // Re-lanzar el error con mensaje específico
      if (error instanceof Error) {
        throw error;
      } else {
        throw new Error('No se pudo consultar la información del usuario en el servidor');
      }
    }
  }

  /**
   * Crear usuario en Realtime Database (igual que en login)
   */
  private async createUserInRealtimeDatabase(apiData: any, eventCode: string): Promise<NetworkingProfile> {
    try {
      // MAPEO RESTAURADO: Consulta SQL corregida en backend, ahora los campos texto son correctos
      const fullName = `${apiData.texto1 || ''} ${apiData.texto2 || ''}`.trim();

      console.log('NetworkingService: Datos completos de la API:', apiData);
      console.log('NetworkingService: PROBLEMA IDENTIFICADO - Campos texto mezclados:');
      console.log('  - texto1 (Nombre):', apiData.texto1, '❌ INCORRECTO');
      console.log('  - texto2 (Apellido):', apiData.texto2, '❌ CONTIENE NOMBRE DEL EVENTO');
      console.log('  - texto3 (Email):', apiData.texto3, '❌ CONTIENE FECHA DEL EVENTO');
      console.log('  - texto8 (Empresa):', apiData.texto8, '❌ UNDEFINED');
      console.log('  - texto9 (Teléfono):', apiData.texto9, '❌ UNDEFINED');
      console.log('NetworkingService: Campos correctos:');
      console.log('  - username:', apiData.username, '✅ CORRECTO');
      console.log('  - company:', apiData.company, '✅ CORRECTO');
      console.log('  - email:', apiData.email, '❌ UNDEFINED');
      console.log('  - phone:', apiData.phone, '❌ UNDEFINED');
      console.log('NetworkingService: Nombre final usado:', fullName);

      // Crear estructura de usuario igual que en login.php
      const timestamp = new Date();

      // MAPEO RESTAURADO: Consulta SQL corregida, ahora usar campos texto correctos
      const mappedEmail = apiData.texto3 || apiData.email || '';
      const mappedPhone = apiData.texto9 || apiData.phone || '';

      console.log('NetworkingService: Mapeo de email:');
      console.log('  - apiData.texto3:', apiData.texto3, '| Tipo:', typeof apiData.texto3);
      console.log('  - apiData.email:', apiData.email, '| Tipo:', typeof apiData.email);
      console.log('  - Email final:', mappedEmail);

      console.log('NetworkingService: Mapeo de teléfono:');
      console.log('  - apiData.texto9:', apiData.texto9, '| Tipo:', typeof apiData.texto9);
      console.log('  - apiData.phone:', apiData.phone, '| Tipo:', typeof apiData.phone);
      console.log('  - Teléfono final:', mappedPhone);

      const userRecord = {
        username: fullName || apiData.username || 'Usuario sin nombre',
        ced: apiData.ced,
        event: eventCode,
        eventcode: eventCode,
        userphoto: "https://www.gravatar.com/avatar?d=mm&s=140",
        start_time: timestamp.toLocaleString('en-US', { timeZone: 'America/Panama' }),
        company: apiData.company || '', // texto8 está undefined, usar campo directo
        role: apiData.tipo_participacion || '',
        email: mappedEmail,
        phone: mappedPhone,
        last_login: timestamp.toISOString(),
        // Campos adicionales para perfil
        position: apiData.tipo_participacion || '',
        profilePhotoUrl: ''
      };

      console.log('NetworkingService: ===== DATOS A GUARDAR EN FIREBASE =====');
      console.log('NetworkingService: userRecord completo:', userRecord);
      console.log('NetworkingService: Campos críticos:');
      console.log('  - ced:', userRecord.ced);
      console.log('  - username:', userRecord.username);
      console.log('  - email:', userRecord.email, '| Tipo:', typeof userRecord.email, '| Vacío:', !userRecord.email);
      console.log('  - phone:', userRecord.phone, '| Tipo:', typeof userRecord.phone, '| Vacío:', !userRecord.phone);
      console.log('  - company:', userRecord.company);
      console.log('  - role:', userRecord.role);
      console.log('NetworkingService: ===== FIN DATOS A GUARDAR =====');

      // La respuesta autenticada de MySQL es suficiente para crear la conexión.
      // Guardar al participante consultado en /user es solo una caché legacy y
      // puede estar prohibido correctamente por las reglas de Realtime Database.
      try {
        const newUserRef = await firebase.database().ref('/user').push(userRecord);
        const newUserId = newUserRef.key;

        if (!newUserId) {
          throw new Error('Firebase no devolvió la clave del usuario');
        }

        console.log('NetworkingService: Usuario guardado como caché con ID:', newUserId);
      } catch (cacheError) {
        console.warn(
          'NetworkingService: Se omitió la caché /user; se utilizarán los datos autorizados de MySQL.',
          cacheError
        );
      }

      // Crear perfil de networking desde los datos
      const profile: NetworkingProfile = {
        id: userRecord.ced,
        name: userRecord.username,
        email: userRecord.email,
        phone: userRecord.phone,
        company: userRecord.company,
        position: userRecord.position || userRecord.role,
        linkedin: '',
        profilePhotoUrl: userRecord.profilePhotoUrl || '',
        eventCode: eventCode,
        createdAt: timestamp,
        updatedAt: timestamp
      };

      console.log('NetworkingService: Perfil final creado:', {
        id: profile.id,
        name: profile.name,
        email: profile.email,
        phone: profile.phone,
        company: profile.company,
        position: profile.position
      });

      console.log('NetworkingService: Perfil creado desde la respuesta de MySQL');
      return profile;

    } catch (error) {
      console.error('NetworkingService: Error al crear usuario en Realtime Database:', error);

      if (error instanceof Error) {
        throw error;
      } else {
        throw new Error('No se pudo crear el usuario');
      }
    }
  }

  /**
   * Verificar si ya existe una conexión entre dos usuarios en Firestore
   */
  private async checkExistingConnection(scannerUserId: string, scannedUserId: string, eventCode: string): Promise<NetworkingProfile | null> {
    try {
      const connectionsRef = firebase.firestore()
        .collection('networking_connections')
        .where('scannerUserId', '==', scannerUserId)
        .where('scannedUserId', '==', scannedUserId)
        .where('eventCode', '==', eventCode);

      const snapshot = await connectionsRef.get();

      if (!snapshot.empty) {
        // Retornar el perfil del usuario escaneado desde la conexión existente
        const connectionData = snapshot.docs[0].data();
        return {
          ...connectionData.scannedProfile,
          eventCode
        } as NetworkingProfile;
      }

      return null;
    } catch (error) {
      console.error('NetworkingService: Error al verificar conexión existente:', error);
      throw error;
    }
  }

  /**
   * Obtener la observación privada guardada por el usuario para una conexión.
   */
  async getConnectionNotes(scannedUserId: string, eventCode: string): Promise<string> {
    const scannerUserId = this.getAuthenticatedFirebaseUid();
    const snapshot = await firebase.firestore()
      .collection('networking_connections')
      .where('scannerUserId', '==', scannerUserId)
      .where('scannedUserId', '==', scannedUserId)
      .where('eventCode', '==', eventCode)
      .get();

    if (snapshot.empty) {
      return '';
    }

    const notes = snapshot.docs[0].data().notes;
    return typeof notes === 'string' ? notes : '';
  }

  /**
   * Guardar una observación privada en la conexión creada por el usuario.
   */
  async updateConnectionNotes(scannedUserId: string, eventCode: string, notes: string): Promise<void> {
    const normalizedNotes = notes.trim();
    if (normalizedNotes.length > 1000) {
      throw new Error('La observación no puede superar los 1.000 caracteres.');
    }

    const scannerUserId = this.getAuthenticatedFirebaseUid();
    const snapshot = await firebase.firestore()
      .collection('networking_connections')
      .where('scannerUserId', '==', scannerUserId)
      .where('scannedUserId', '==', scannedUserId)
      .where('eventCode', '==', eventCode)
      .get();

    if (snapshot.empty) {
      throw new Error('No se encontró la conexión de networking.');
    }

    await snapshot.docs[0].ref.update({ notes: normalizedNotes });
  }

  /**
   * Registrar una conexión de networking en Firestore
   */
  private async recordConnection(scannerProfile: NetworkingProfile, scannedProfile: NetworkingProfile): Promise<void> {
    try {
      const scannerAuthUid = this.getAuthenticatedFirebaseUid();
      const connectionData = {
        // El propietario siempre es el UID autenticado. `scannerProfile.id`
        // puede ser una cédula o un identificador local heredado.
        scannerUserId: scannerAuthUid,
        scannedUserId: scannedProfile.id,
        scannerProfile: {
          id: scannerProfile.id,
          name: scannerProfile.name,
          email: scannerProfile.email,
          phone: scannerProfile.phone,
          company: scannerProfile.company,
          position: scannerProfile.position
        },
        scannedProfile: {
          id: scannedProfile.id,
          name: scannedProfile.name,
          email: scannedProfile.email,
          phone: scannedProfile.phone,
          company: scannedProfile.company,
          position: scannedProfile.position
        },
        scannedAt: firebase.firestore.FieldValue.serverTimestamp(),
        eventCode: scannerProfile.eventCode,
        notes: ''
      };

      // Guardar en Firestore
      await firebase.firestore()
        .collection('networking_connections')
        .add(connectionData);

      console.log('NetworkingService: Conexión registrada exitosamente en Firestore');
    } catch (error) {
      console.error('NetworkingService: Error al registrar conexión:', error);
      throw error;
    }
  }

  /**
   * Obtener todos los contactos escaneados desde Firestore
   */
  async getScannedContacts(): Promise<NetworkingScan[]> {
    try {
      const currentProfile = await this.getCurrentProfile();
      if (!currentProfile) {
        return [];
      }

      console.log('NetworkingService: Obteniendo contactos escaneados desde Firestore');
      const scannerAuthUid = this.getAuthenticatedFirebaseUid();

      // Consultar conexiones desde Firestore (sin orderBy para evitar índice compuesto)
      const connectionsRef = firebase.firestore()
        .collection('networking_connections')
        .where('scannerUserId', '==', scannerAuthUid)
        .where('eventCode', '==', currentProfile.eventCode);

      const snapshot = await connectionsRef.get();
      const connections: NetworkingScan[] = [];

      snapshot.forEach(doc => {
        const data = doc.data();
        const connection: NetworkingScan = {
          id: doc.id,
          scannerUserId: data.scannerUserId,
          scannedUserId: data.scannedUserId,
          eventCode: data.eventCode,
          scannerProfile: {
            id: data.scannerProfile.id,
            name: data.scannerProfile.name,
            email: data.scannerProfile.email,
            phone: data.scannerProfile.phone,
            company: data.scannerProfile.company,
            position: data.scannerProfile.position,
            linkedin: '',
            eventCode: currentProfile.eventCode,
            createdAt: new Date(),
            updatedAt: new Date()
          },
          scannedProfile: {
            id: data.scannedProfile.id,
            name: data.scannedProfile.name,
            email: data.scannedProfile.email,
            phone: data.scannedProfile.phone,
            company: data.scannedProfile.company,
            position: data.scannedProfile.position,
            linkedin: '',
            eventCode: currentProfile.eventCode,
            createdAt: new Date(),
            updatedAt: new Date()
          },
          scannedAt: data.scannedAt?.toDate() || new Date(),
          notes: data.notes || ''
        };
        connections.push(connection);
      });

      // Ordenar por fecha de escaneo (más reciente primero) en el cliente
      connections.sort((a, b) => b.scannedAt.getTime() - a.scannedAt.getTime());

      console.log('NetworkingService: Contactos obtenidos desde Firestore:', connections.length);
      return connections;
    } catch (error) {
      console.error('NetworkingService: Error al obtener contactos desde Firestore:', error);
      return [];
    }
  }

  /**
   * Obtener estadísticas de networking
   */
  async getNetworkingStats(): Promise<NetworkingStats> {
    try {
      const contacts = await this.getScannedContacts();
      
      const stats: NetworkingStats = {
        totalScans: contacts.length,
        uniqueContacts: contacts.length, // Cada escaneo es único si no permitimos duplicados
        lastScanDate: contacts.length > 0 ? contacts[0].scannedAt : undefined,
        topCompanies: []
      };

      // Calcular empresas más frecuentes
      const companyCount: { [key: string]: number } = {};
      contacts.forEach(contact => {
        const company = contact.scannedProfile.company || 'Sin empresa';
        companyCount[company] = (companyCount[company] || 0) + 1;
      });

      stats.topCompanies = Object.entries(companyCount)
        .map(([company, count]) => ({ company, count }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 5);

      return stats;
    } catch (error) {
      console.error('NetworkingService: Error al obtener estadísticas:', error);
      return {
        totalScans: 0,
        uniqueContacts: 0,
        topCompanies: []
      };
    }
  }

  /**
   * Limpiar datos locales
   */
  async clearLocalData(): Promise<void> {
    try {
      await Storage.remove({ key: 'networking_profile' });
      this.currentProfile = null;
      console.log('NetworkingService: Datos locales limpiados');
    } catch (error) {
      console.error('NetworkingService: Error al limpiar datos locales:', error);
    }
  }

  /**
   * Función de prueba para verificar el mapeo de campos
   */
  testFieldMapping(apiData: any): void {
    console.log('=== PRUEBA DE MAPEO DE CAMPOS ===');
    console.log('Datos de entrada:', apiData);

    const fullName = `${apiData.texto1 || ''} ${apiData.texto2 || ''}`.trim();

    const mappedData = {
      name: fullName || apiData.username || 'Usuario sin nombre',
      email: apiData.texto3 || apiData.email || '',
      phone: apiData.texto9 || apiData.phone || '',
      company: apiData.texto8 || apiData.company || '',
      position: apiData.tipo_participacion || ''
    };

    console.log('Mapeo aplicado:');
    console.log('  texto1 + texto2 → name:', mappedData.name);
    console.log('  texto3 → email:', mappedData.email);
    console.log('  texto8 → company:', mappedData.company);
    console.log('  texto9 → phone:', mappedData.phone);
    console.log('  tipo_participacion → position:', mappedData.position);
    console.log('=== FIN PRUEBA ===');
  }

  /**
   * Función de debug para probar con una cédula específica
   */
  async debugUserLookup(cedula: string): Promise<void> {
    try {
      console.log('=== DEBUG USER LOOKUP ===');
      console.log('Buscando usuario con cédula:', cedula);

      const currentProfile = await this.getCurrentProfile();
      if (!currentProfile) {
        console.log('No hay perfil actual configurado');
        return;
      }

      console.log('Evento actual:', currentProfile.eventCode);

      // Hacer la consulta a la API directamente
      const apiData = await this.fetchUserFromAPI(cedula, currentProfile.eventCode);
      console.log('Datos recibidos de la API:', apiData);

      // Probar el mapeo
      this.testFieldMapping(apiData);

      console.log('=== FIN DEBUG ===');
    } catch (error) {
      console.error('Error en debug lookup:', error);
    }
  }

  /**
   * Función para encontrar todos los usuarios con una cédula específica
   */
  async findAllUsersWithCedula(cedula: string): Promise<void> {
    try {
      console.log('=== BUSCANDO TODOS LOS USUARIOS CON CÉDULA ===');
      console.log('Cédula a buscar:', cedula);

      const usersSnapshot = await firebase.database().ref('/user').once('value');

      if (usersSnapshot.exists()) {
        const allUsers = usersSnapshot.val();
        let foundCount = 0;

        for (const userId in allUsers) {
          const userData = allUsers[userId];
          if (userData.ced === cedula) {
            foundCount++;
            console.log(`=== USUARIO ${foundCount} ENCONTRADO ===`);
            console.log('ID del nodo:', userId);
            console.log('Datos completos:', userData);
            console.log('Campos clave:');
            console.log('  - ced:', userData.ced);
            console.log('  - username:', userData.username);
            console.log('  - email:', userData.email);
            console.log('  - company:', userData.company);
            console.log('  - eventcode:', userData.eventcode);
            console.log('  - start_time:', userData.start_time);
            console.log('  - last_login:', userData.last_login);
            console.log('========================');
          }
        }

        console.log(`Total de usuarios encontrados con cédula ${cedula}: ${foundCount}`);
      } else {
        console.log('No hay usuarios en la base de datos');
      }

      console.log('=== FIN BÚSQUEDA DE USUARIOS ===');
    } catch (error) {
      console.error('Error en búsqueda de usuarios:', error);
    }
  }

  /**
   * Debug específico para cualquier cédula
   */
  async debugUserData(cedula: string): Promise<void> {
    try {
      console.log('=== DEBUG DATOS DE USUARIO ===');
      console.log('Cédula:', cedula);

      const currentProfile = await this.getCurrentProfile();
      if (!currentProfile) {
        console.log('No hay perfil actual configurado');
        return;
      }

      console.log('Evento:', currentProfile.eventCode);

      // 1. Consultar API directamente
      console.log('--- PASO 1: CONSULTANDO API ---');
      const requestData = configService.buildSingleEventLoginRequest(cedula, currentProfile.eventCode);
      const requestOptions = configService.getLoginRequestOptions({
        ...requestData,
        action: 'get_user_info'
      });

      const response = await fetch(configService.getLoginEndpoint(), requestOptions);

      const apiData = await response.json();
      console.log('Respuesta RAW de la API:', apiData);

      // 2. Verificar campos específicos
      console.log('--- PASO 2: ANÁLISIS DE CAMPOS ---');
      console.log('Campos de texto:');
      for (let i = 1; i <= 10; i++) {
        const field = `texto${i}`;
        if (apiData[field] !== undefined) {
          console.log(`  - ${field}:`, apiData[field], '| Tipo:', typeof apiData[field]);
        }
      }

      console.log('Campos directos:');
      ['username', 'email', 'phone', 'company', 'ced', 'eventcode', 'tipo_participacion'].forEach(field => {
        if (apiData[field] !== undefined) {
          console.log(`  - ${field}:`, apiData[field], '| Tipo:', typeof apiData[field]);
        }
      });

      // 3. Buscar en Firebase
      console.log('--- PASO 3: BUSCANDO EN FIREBASE ---');
      await this.findAllUsersWithCedula(cedula);

      console.log('=== FIN DEBUG DATOS DE USUARIO ===');
    } catch (error) {
      console.error('Error en debug de datos de usuario:', error);
    }
  }

  /**
   * Debug específico para la cédula problemática
   */
  async debugProblematicUser(): Promise<void> {
    await this.debugUserData('00112709860');
  }

  /**
   * Función para limpiar TODOS los usuarios con datos corruptos
   */
  async cleanAllCorruptedUsers(): Promise<void> {
    try {
      console.log('=== LIMPIANDO TODOS LOS USUARIOS CORRUPTOS ===');

      const currentProfile = await this.getCurrentProfile();
      if (!currentProfile) {
        console.log('No hay perfil actual configurado');
        return;
      }

      const usersSnapshot = await firebase.database().ref('/user').once('value');

      if (usersSnapshot.exists()) {
        const allUsers = usersSnapshot.val();
        let deletedCount = 0;

        for (const userId in allUsers) {
          const userData = allUsers[userId];

          // Identificar usuarios corruptos: username contiene nombre del evento
          if (userData.username && userData.username.includes('Congreso Centroamericano')) {
            console.log(`Eliminando usuario corrupto ${userId}:`, userData.username);
            await firebase.database().ref(`/user/${userId}`).remove();
            deletedCount++;
          }
        }

        console.log(`Total de usuarios corruptos eliminados: ${deletedCount}`);
      }

      console.log('=== FIN LIMPIEZA DE USUARIOS CORRUPTOS ===');
    } catch (error) {
      console.error('Error en limpieza de usuarios corruptos:', error);
    }
  }

  /**
   * Función para limpiar y recrear un usuario específico
   */
  async cleanAndRecreateUser(cedula: string): Promise<void> {
    try {
      console.log('=== LIMPIANDO Y RECREANDO USUARIO ===');
      console.log('Cédula:', cedula);

      const currentProfile = await this.getCurrentProfile();
      if (!currentProfile) {
        console.log('No hay perfil actual configurado');
        return;
      }

      // 1. Buscar y eliminar el usuario existente en Firebase
      console.log('Paso 1: Buscando usuario existente en Firebase...');
      const usersSnapshot = await firebase.database().ref('/user').once('value');

      if (usersSnapshot.exists()) {
        const allUsers = usersSnapshot.val();

        for (const userId in allUsers) {
          const userData = allUsers[userId];
          if (userData.ced === cedula && userData.eventcode === currentProfile.eventCode) {
            console.log('Usuario encontrado en Firebase, eliminando:', userId);
            await firebase.database().ref(`/user/${userId}`).remove();
            console.log('Usuario eliminado de Firebase');
            break;
          }
        }
      }

      // 2. Consultar datos frescos de la API
      console.log('Paso 2: Consultando datos frescos de la API...');
      const apiData = await this.fetchUserFromAPI(cedula, currentProfile.eventCode);

      // 3. Crear usuario nuevo con datos frescos
      console.log('Paso 3: Creando usuario con datos frescos...');
      const newProfile = await this.createUserInRealtimeDatabase(apiData, currentProfile.eventCode);

      console.log('Usuario recreado exitosamente:', newProfile);
      console.log('=== FIN LIMPIEZA Y RECREACIÓN ===');
    } catch (error) {
      console.error('Error en limpieza y recreación:', error);
    }
  }
}

export default NetworkingService;
